import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../../../models/DraftStore';
import { AppBar, SvgIcon } from '../../../Molecules';
import { isNotEmptyString } from '../../../../utils/string';

import styles from './CreateMemoryGameEnterContentTemplate.module.css';
import { TextArea } from '../../../Molecules/TextArea';

type TextInputProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

interface CreateMemoryGameEnterContentTemplateProps {
  type: QuickCreateGameMode | undefined;
  language: string;
  gameContents: TextInputProps[];
  isNextDisabled: boolean;
  isExceedLimit: boolean;
  showRequiredWarning: boolean;
  onClickBack: () => void;
  onClickLanguage: () => void;
  onClickExample: () => void;
  onClickRemove: (index: number) => void;
  onClickScanText: (index: number) => void;
  onClickAddContent: () => void;
  onClickNext: () => void;
}

export const CreateMemoryGameEnterContentTemplate = (
  props: CreateMemoryGameEnterContentTemplateProps,
) => {
  const {
    type,
    language,
    gameContents,
    isNextDisabled,
    isExceedLimit,
    showRequiredWarning,
    onClickBack,
    onClickLanguage,
    onClickExample,
    onClickRemove,
    onClickScanText,
    onClickAddContent,
    onClickNext,
  } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.createMemoryEnterContent}>
      <AppBar
        title={type ? t(getQuickCreateGameModeProps(type).name) : ''}
        onClickBack={onClickBack}
      />
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          {/* <p className={styles.gameTypeName}>
            {t(getQuickCreateGameModeProps(type).name)}
          </p> */}
          <p className={styles.gameTypeName}>{t('Original Content')}</p>
        </div>

        <div className={styles.buttonContainer} onClick={onClickLanguage}>
          <div className={styles.languageSelector}>
            <SvgIcon
              name={SVGIconType.LanguageA}
              width={28}
              height={28}
              stroke={'white'}
            />
            <span className={styles.language}>{t(language)}</span>
            <SvgIcon
              name={SVGIconType.Triangle}
              width={20}
              height={20}
              stroke={'white'}
              fill={'white'}
            />
          </div>
        </div>

        <div className={styles.cellHeader}>
          <p className={styles.title}>{t('Learning Content')}</p>
          <button className={styles.exampleButton} onClick={onClickExample}>
            {t('Examples')}
          </button>
        </div>

        {gameContents.map((gameContent, index) => (
          <div key={index} className={styles.textSection}>
            <div className={styles.gameFooter}>
              <div className={styles.gameNumber}>{index + 1}</div>
              <div
                className={styles.scanText}
                onClick={() => {
                  onClickScanText(index);
                }}
              >
                <SvgIcon
                  name={SVGIconType.OCRScan}
                  width={24}
                  height={24}
                  stroke="#34B9AE"
                />
                {t('Scan Text from Image')}
              </div>
              {gameContents.length > 1 ? (
                <button
                  className={styles.removeButton}
                  onClick={() => {
                    onClickRemove(index);
                  }}
                >
                  <SvgIcon
                    name={SVGIconType.CircleCross}
                    width={24}
                    height={24}
                  />
                </button>
              ) : (
                <div className={styles.emptyRemoveButton} />
              )}
            </div>
            <TextArea
              textEntryClassName={styles.textEntry}
              textEntryAreaClassName={styles.textEntryArea}
              errorMessageClassName={styles.warningText}
              value={gameContent.value}
              onChange={gameContent.onChange}
              isAutoResize={true}
              placeholder={t('Enter vocabulary, sentences or paragraphs')}
              errorMessage={
                showRequiredWarning && !isNotEmptyString(gameContent.value)
                  ? t('This field is required.')
                  : undefined
              }
            />
          </div>
        ))}

        <button
          className={styles.addButton}
          onClick={onClickAddContent}
        >{`+ ${t('Content')}`}</button>
        <p
          className={styles.gameCount}
          style={{ color: isExceedLimit ? '#D95141' : '#808080' }}
        >
          {t('(max. {count})', { count: 20 })}
        </p>
      </div>
      <div className={styles.bottomBar}>
        <button
          className={styles.okButton}
          onClick={onClickNext}
          disabled={isNextDisabled}
        >
          <span className={styles.okText}>{t('Next')}</span>
          <SvgIcon className={styles.rightArrowIcon} name={SVGIconType.Arrow} />
          {isNextDisabled ? <div className={styles.blackShadow} /> : null}
        </button>
      </div>
    </div>
  );
};
